import { useMutation } from '@moonpig/web-core-graphql'
import {
  trackGAEvent,
  createBasketEvent,
  trackUIEvent,
  createCustomisationCompletedEvent,
} from '@moonpig/web-core-analytics'
import { DepartmentsEnum } from '@moonpig/web-shared-types-graphql/graphqlTypes'
import { getBrowserCookies } from '@moonpig/web-core-browser-storage'
import { Region } from '@moonpig/web-core-types'
import { getDeviceType } from '@moonpig/web-shared-utils'
import { isDigitalGiftCard } from '../isDigitalGift/isDigitalGift'
import { LineItem } from './types'
import { addDigitalGiftCardToBasket } from '../../queries/addDigitalGiftToBasket/addDigitalGiftCardToBasket'
import { addDigitalGiftToBasket_addDigitalGiftToBasket_Basket } from '../types-graphql'
import { Variant } from '../../types'

export const useAddDigitalGiftToBasket = () => {
  const [addDigitalGiftToBasketMutation] = useMutation(
    addDigitalGiftCardToBasket,
  )

  const addToBasket = async ({
    variant,
    lastLineItem,
    basketId,
    region,
    customerId,
  }: {
    variant: Variant
    lastLineItem?: Pick<LineItem, 'id' | 'productType'> | null
    basketId: string
    region: Region
    customerId?: string | undefined
  }): Promise<{
    addToBasketData: addDigitalGiftToBasket_addDigitalGiftToBasket_Basket
  }> => {
    if (!variant.sku || !lastLineItem) {
      throw Error('Add digital gift to basket validation failed')
    }

    if (lastLineItem?.productType !== 'greetingcard') {
      throw Error(
        'Add digital gift to basket failed as lastLineItem is not a card',
      )
    }

    const { data } = await addDigitalGiftToBasketMutation({
      variables: {
        input: {
          sku: variant.sku,
          parentLineItemId: lastLineItem.id,
          id: basketId,
        },
      },
    })

    if (
      data?.addDigitalGiftToBasket === null ||
      data?.addDigitalGiftToBasket?.__typename !== 'Basket'
    ) {
      const errorMessage = `${data?.addDigitalGiftToBasket?.__typename}: ${data?.addDigitalGiftToBasket?.message}`
      throw Error(errorMessage)
    }

    const { mnpg_ui_events_api_correlation_id: correlationId } =
      getBrowserCookies()

    trackUIEvent(
      createCustomisationCompletedEvent({
        correlationId,
        productId: variant.sku.split('-')[0],
        store: region.toUpperCase(),
        source: getDeviceType(),
        customerId,
      }),
    )

    return { addToBasketData: data.addDigitalGiftToBasket }
  }

  const trackAddDigitalGiftToBasket = (
    variant: Variant,
    totalBasketSize: number,
    basketId: string,
    department: DepartmentsEnum,
  ) => {
    const price = variant.price.centAmount / 100
    const formattedPrice = price.toFixed(2)
    const sku = variant.sku?.toLowerCase()
    const item = {
      item_brand: 'moonpig',
      item_name: variant.title.toLocaleLowerCase(),
      item_id: sku ?? /* istanbul ignore next */ '',
      item_variant: isDigitalGiftCard(department)
        ? variant.price.centAmount.toString()
        : variant.title,
      item_category: DepartmentsEnum.DIGITAL_GIFTS.toLocaleLowerCase().replace(
        '_',
        ' ',
      ),

      item_category2: 'all_gifts',
      item_category3: 'in-stock',
      item_category4: 'non-group-card',
      item_category5: undefined,
      item_list_name: undefined,
      index: undefined,
      quantity: 1,
      price,
    }

    trackGAEvent(
      createBasketEvent({
        event: 'add_to_cart',
        products: [item],
        basket: {
          size: totalBasketSize,
          action: 'add to cart',
          id: basketId.toLowerCase(),
        },
        action: 'add to basket',
        currency: variant.price.currencyCode.toLocaleLowerCase(),
        totalValue: Number(formattedPrice),
        label: `digital gift | ${sku}`,
        productIndex: undefined,
        eventCategory: 'product action',
      }),
    )
  }

  const addDigitalGiftToBasket = async ({
    lastLineItem,
    variant,
    basketId,
    region,
    customerId,
    department,
  }: {
    variant: Variant
    basketId: string
    lastLineItem?: Pick<LineItem, 'id' | 'productType'> | null
    region: Region
    customerId?: string | undefined
    department: DepartmentsEnum
  }) => {
    const { addToBasketData } = await addToBasket({
      variant,
      lastLineItem,
      basketId,
      region,
      customerId,
    })
    trackAddDigitalGiftToBasket(
      variant,
      addToBasketData.totalItems,
      basketId,
      department,
    )

    return { data: addToBasketData }
  }

  return {
    addDigitalGiftToBasket,
  }
}
